import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Zap } from "lucide-react";
import LoginModal from "@/components/LoginModal";

// BMC functionality
const BMC_URL = import.meta.env.VITE_BMC_URL || 'https://www.buymeacoffee.com/toolref';
const openBmc = (e?: React.MouseEvent) => {
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }
  window.open(BMC_URL, '_blank', 'noopener,noreferrer');
};

function CTA() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  return (
    <motion.div 
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="w-full py-20 lg:py-40"
    >
      <div className="container mx-auto">
        <motion.div 
          initial={{ scale: 0.95, opacity: 0 }}
          animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          className="flex flex-col text-center bg-muted rounded-md p-4 lg:p-14 gap-8 items-center"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Badge className="bg-accent-light text-accent">Ready to unlock everything?</Badge>
          </motion.div>
          <motion.div 
            className="flex flex-col gap-2"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <h2 className="text-3xl md:text-5xl tracking-tighter max-w-xl font-medium font-poppins">
              Get Lifetime Access to All Design Tools
            </h2>
            <p className="text-lg leading-relaxed tracking-tight text-muted-foreground max-w-xl font-inter">
              One-time payment for unlimited access to our complete directory. Browse 300+ curated design tools, no subscriptions required.
            </p>
          </motion.div>
          <motion.div 
            className="flex flex-row gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <Link to="/unlock">
              <Button 
                className="bg-gradient-to-r from-primary to-primary-glow hover:from-primary-glow hover:to-primary text-primary-foreground font-semibold"
              >
                <Zap className="mr-2 h-4 w-4" />
                Get Lifetime Access
              </Button>
            </Link>
            <Button 
              variant="outline"
              onClick={() => setIsLoginModalOpen(true)}
            >
              Login
            </Button>
          </motion.div>
        </motion.div>
      </div>
      <LoginModal 
        isOpen={isLoginModalOpen} 
        onClose={() => setIsLoginModalOpen(false)} 
      />
    </motion.div>
  );
}

export { CTA };